import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import 'maplibre-gl/dist/maplibre-gl.css';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { GuestBottomNav } from '../../components/GuestBottomNav';
import { DirectionsIntentModal } from '../../components/guest-map/GuestMapSurface';
import { VirtualizedVenueList } from '../../components/guest-map/VirtualizedVenueList';
import { VenueCard } from '../../components/guest-map/VenueCard';
import { GuestMapHeader } from '../../components/guest-map/GuestMapHeader';
import { GuestMapSearchBar } from '../../components/guest-map/GuestMapSearchBar';
import { GuestMapCategoryChips } from '../../components/guest-map/GuestMapCategoryChips';
import { GuestMapLocationBadge } from '../../components/guest-map/GuestMapLocationBadge';
import { GuestMapStylesheet } from '../../components/guest-map/GuestMapStylesheet';
import { GuestMapListHeader } from '../../components/guest-map/GuestMapListHeader';
import { MapFixedSection } from '../../components/guest-map/MapFixedSection';
import {
  HEADER_HEIGHT, MAP_ANIM_MS, MINI_MAP_HEIGHT, MINI_MAP_HEIGHT_COMPACT,
  NAV_HEIGHT_COMPACT, NEARBY_RADIUS_KM,
} from '../../components/guest-map/guestMapConstants';
import { useUserLocation } from '../../hooks/useUserLocation';
import { useNearbyEvents } from '../../hooks/useNearbyEvents';
import { useScrollNavCompact } from '../../hooks/useScrollNavCompact';
import { useFavoriteVenues } from '../../hooks/useFavoriteVenues';
import { useGuestMapKeyboard } from '../../hooks/useGuestMapKeyboard';
import { useGuestMapDrag } from '../../hooks/useGuestMapDrag';
import { useTagRefit } from '../../hooks/useTagRefit';
import { useGuestMapMarkers } from '../../hooks/useGuestMapMarkers';
import { useGuestMapInstance } from '../../hooks/useGuestMapInstance';
import { useGuestMapVenueData } from '../../hooks/useGuestMapVenueData';
import { useGuestMapExperienceData } from '../../hooks/useGuestMapExperienceData';
import { useGuestActivePass } from '../../hooks/useGuestActivePass';
import { useGuestDiscount } from '../../hooks/useGuestDiscount';
import { useEnRouteChargesBoot } from '../../hooks/useEnRouteChargesBoot';
import { useHeaderBottomMeasure } from '../../hooks/useHeaderBottomMeasure';
import { useGuestMapPeek } from '../../hooks/useGuestMapPeek';
import { useOpenTargetVenue } from '../../hooks/useOpenTargetVenue';
import { useLocalUserProfile } from '../../hooks/useLocalUserProfile';
import { useOpenVenueChat } from '../../hooks/useOpenVenueChat';
import { useEventVenueTap } from '../../hooks/useEventVenueTap';
import { useTheme } from '../../contexts/ThemeContext';

export default function GuestMapPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isDark } = useTheme();
  const { userPosition, locationStatus, defaultCenter, requestLocation } = useUserLocation();

  // ---- Local UI state --------------------------------------------------
  const [searchInput, setSearchInput] = useState('');
  const [selectedVenueTags, setSelectedVenueTags] = useState([]);
  const [selectedExperienceTags, setSelectedExperienceTags] = useState([]);
  const [experienceTagOn, setExperienceTagOn] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [savedOnly, setSavedOnly] = useState(false);
  const [hiddenGemsOn, setHiddenGemsOn] = useState(true);
  const [selectedExperienceId, setSelectedExperienceId] = useState('');
  const [expandedVenueId, setExpandedVenueId] = useState(null);
  const [selectedMapVenueId, setSelectedMapVenueId] = useState(null);
  const [pinnedVenueId, setPinnedVenueId] = useState(null);

  // ---- Refs ------------------------------------------------------------
  const mapCanvasRef = useRef(null);
  const markersRef = useRef(new Map());
  const experienceMarkersRef = useRef(new Map());
  const userMarkerRef = useRef(null);
  const venueListRef = useRef(null);
  const headerGroupRef = useRef(null);
  // Live isExpanded ref — synced from the drag hook below. Kept at the
  // page level so multiple downstream hooks (venue-data filter, marker
  // click handler) can consume it without prop-drilling every setter.
  const isExpandedRef = useRef(false);


  // ---- Composable hooks ------------------------------------------------
  const { firstName, userAvatarUrl } = useLocalUserProfile();
  const {
    keyboardOpen, setKeyboardOpen, viewportReduced,
  } = useGuestMapKeyboard();
  const navCompact = useScrollNavCompact();
  const { favoriteIds, favoriteCount, isFavorite } = useFavoriteVenues();
  const currentDiscount = useGuestDiscount();
  const hasActivePass = useGuestActivePass();
  useEnRouteChargesBoot();
  const { openVenueChat, openingVenueId } = useOpenVenueChat({ hasActivePass });

  const headerBottom = useHeaderBottomMeasure(headerGroupRef, HEADER_HEIGHT + 24);
  const effectiveMiniHeight = navCompact ? MINI_MAP_HEIGHT_COMPACT : MINI_MAP_HEIGHT;
  const effectiveNavOffset = NAV_HEIGHT_COMPACT;
  const mapTheme = isDark ? 'dark' : 'light';
  const isDarkMode = mapTheme === 'dark';
  const markerSize = 44;

  const getMaxHeight = useCallback(() => {
    if (typeof window !== 'undefined') {
      return window.innerHeight - headerBottom - effectiveNavOffset;
    }
    return 500;
  }, [headerBottom, effectiveNavOffset]);

  const {
    allVenues, setAllVenues, visibleVenues, sortedVenues, featuredVenues,
    loading, handleBoundsChange, distanceKmFromUser,
  } = useGuestMapVenueData({
    selectedVenueTags,
    selectedCategory,
    pinnedVenueId,
    savedOnly,
    favoriteIds,
    userPosition,
    locationStatus,
    isExpandedRef,
  });

  const { experiences, setExperiences, displayedExperiences, nearbyExperienceCount } = useGuestMapExperienceData({
    userPosition,
    locationStatus,
    hiddenGemsOn,
    selectedExperienceTags,
  });

  const { nearbyEvents, eventsByVenueId, venueEventState } = useNearbyEvents({
    userPosition: locationStatus === 'live' ? userPosition : null,
    radiusKm: NEARBY_RADIUS_KM,
  });

  const { mapRef, centerOnMe, toLngLat } = useGuestMapInstance({
    mapCanvasRef,
    mapTheme,
    userPosition,
    defaultCenter,
    locationStatus,
    requestLocation,
    onBoundsChange: handleBoundsChange,
    onMapClick: useCallback(() => {
      setSelectedMapVenueId(null);
      setSelectedExperienceId('');
    }, []),
    markersRef,
    experienceMarkersRef,
    userMarkerRef,
  });

  const {
    mapHeight, isDragging, isExpanded, isResizing, hasInteractedRef,
    animateMapResize, openMapFully, minimizeMap,
    onPointerDown, onPointerMove, onPointerUp,
  } = useGuestMapDrag({
    mapRef,
    getMaxHeight,
    effectiveMiniHeight,
    setSelectedMapVenueId,
    animMs: MAP_ANIM_MS,
  });

  useEffect(() => { isExpandedRef.current = isExpanded; }, [isExpanded]);

  const {
    applyPulseToMarker,
    pendingPulseVenueIdsRef,
    pendingPulseExperienceIdsRef,
  } = useTagRefit({
    mapRef,
    markersRef,
    experienceMarkersRef,
    allVenues,
    experiences,
    selectedVenueTags,
    selectedExperienceTags,
    locationStatus,
    userPosition,
    radiusKm: NEARBY_RADIUS_KM,
    layoutReady: isExpanded && !isResizing && !keyboardOpen && !viewportReduced,
    layoutHeight: mapHeight,
  });

  // Marker rendering — MUST be after `displayedExperiences` is computed
  // (via useGuestMapExperienceData above) to avoid a TDZ ReferenceError
  // in the hook parameters.
  useGuestMapMarkers({
    mapRef,
    markersRef,
    experienceMarkersRef,
    userMarkerRef,
    isExpandedRef,
    visibleVenues,
    displayedExperiences,
    selectedMapVenueId,
    selectedExperienceId,
    setSelectedMapVenueId,
    setSelectedExperienceId,
    openMapFully,
    userPosition,
    userAvatarUrl,
    locationStatus,
    distanceKmFromUser,
    isFavorite,
    venueEventState,
    markerSize,
    nearbyRadiusKm: NEARBY_RADIUS_KM,
    applyPulseToMarker,
    pendingPulseVenueIdsRef,
    pendingPulseExperienceIdsRef,
    toLngLat,
  });

  useGuestMapPeek({ loading, miniHeight: MINI_MAP_HEIGHT, animateMapResize, hasInteractedRef });
  useOpenTargetVenue({
    location, navigate,
    sortedVenues, allVenues, setAllVenues,
    setPinnedVenueId, setExpandedVenueId, venueListRef,
  });

  const onEventVenueTap = useEventVenueTap({
    allVenues, setAllVenues, setPinnedVenueId, setExpandedVenueId, venueListRef,
  });

  const totalSectionHeight = mapHeight + effectiveNavOffset;

  // ---- Handlers --------------------------------------------------------
  const scrollExpandedVenueIntoView = (venueId) => {
    if (!venueId) return;
    const inList = sortedVenues.some((v) => v.id === venueId);
    if (!inList) setPinnedVenueId(venueId);
    setExpandedVenueId(venueId);
    setTimeout(() => {
      venueListRef.current?.scrollToVenue(venueId);
    }, inList ? 0 : 360);
  };

  const renderVenueItem = useCallback((v, idx, isRowExpanded) => (
    <VenueCard
      venue={v}
      idx={idx}
      isExpanded={isRowExpanded}
      userPosition={userPosition}
      venueEvents={eventsByVenueId.get(v.id) || []}
      onClick={() => setExpandedVenueId(v.id)}
      onClose={() => {
        setExpandedVenueId(null);
        setPinnedVenueId((pid) => (pid === v.id ? null : pid));
      }}
      onMessage={openVenueChat}
      hasActivePass={hasActivePass}
    />
  ), [userPosition, eventsByVenueId, openVenueChat, hasActivePass]);

  // ---- Search-bar handlers --------------------------------------------
  const commitTag = (tag, kind) => {
    const total = selectedVenueTags.length + selectedExperienceTags.length;
    if (total >= 3) {
      toast('You can pick up to 3 tags', { id: 'tag-cap', duration: 2200 });
      return;
    }
    if (kind === 'venue') {
      setSelectedVenueTags((prev) => (prev.includes(tag) ? prev : [...prev, tag]));
    } else {
      setSelectedExperienceTags((prev) => (prev.includes(tag) ? prev : [...prev, tag]));
      setHiddenGemsOn(true);
    }
    setSearchInput('');
    // Close any open preview so the new tag's animation doesn't visually
    // land on the previously-selected pin.
    setSelectedMapVenueId(null);
    setSelectedExperienceId('');
    // Record intent immediately; the drag hook follows the restoring window.
    setKeyboardOpen(false);
    openMapFully();
  };

  const onSearchFocus = () => {
    // If the map is expanded when the guest taps the search bar, minimize
    // it BEFORE the on-screen keyboard opens. Otherwise the fixed map
    // stays huge and the browser's "scroll input into view" push shoves
    // the search bar off-screen.
    minimizeMap();
    // Optimistically hide the map + nav the instant the guest taps the
    // input so there's no visible "slide up over the keyboard" jitter.
    // The visualViewport listener flips this back to false the moment
    // the keyboard actually closes (including via Android system-back,
    // which does NOT blur the input — onBlur alone isn't enough).
    setKeyboardOpen(true);
  };

  const onSearchBlur = () => {
    setKeyboardOpen(false);
  };

  const onToggleHiddenGems = () => {
    if (locationStatus !== 'live' || !userPosition) {
      toast('Enable location to see nearby gems', {
        id: 'gems-location-gate',
        duration: 2600,
      });
      return;
    }
    setHiddenGemsOn((value) => !value);
  };

  // ---- Render ----------------------------------------------------------
  return (
    <div className="app-shell" data-testid="guest-map-page" style={{
      // `100dvh` (dynamic viewport height) shrinks when the mobile
      // keyboard opens, so the app-shell stays inside the visible
      // viewport. Combined with `overflow: hidden` it pins the header
      // even while the IME is up — the only region that scrolls is the
      // virtualized venue list.
      minHeight: '100vh',
      height: '100dvh',
      maxHeight: '100dvh',
      overflow: 'hidden',
      backgroundColor: 'var(--surface)',
      display: 'flex',
      flexDirection: 'column',
      paddingTop: 0,
    }}>
      <div ref={headerGroupRef} data-testid="guest-map-header-group" style={{ flexShrink: 0 }}>
        <GuestMapHeader firstName={firstName} currentDiscount={hasActivePass ? currentDiscount : null} />
        <GuestMapSearchBar
          inputValue={searchInput}
          onInputChange={setSearchInput}
          selectedVenueTags={selectedVenueTags}
          selectedExperienceTags={selectedExperienceTags}
          onAddVenueTag={(tag) => commitTag(tag, 'venue')}
          onAddExperienceTag={(tag) => commitTag(tag, 'experience')}
          onRemoveVenueTag={(tag) => setSelectedVenueTags((prev) => prev.filter((t) => t !== tag))}
          onRemoveExperienceTag={(tag) => setSelectedExperienceTags((prev) => prev.filter((t) => t !== tag))}
          venues={allVenues}
          experiences={experiences}
          userPosition={userPosition}
          experienceTagOn={experienceTagOn}
          onToggleExperienceTag={() => setExperienceTagOn((v) => !v)}
          onFocus={onSearchFocus}
          onBlur={onSearchBlur}
        />
        <GuestMapCategoryChips
          selected={selectedCategory}
          onSelect={setSelectedCategory}
          venues={allVenues}
          userPosition={userPosition}
        />
        <GuestMapLocationBadge
          hiddenGemsOn={hiddenGemsOn}
          onToggleHiddenGems={onToggleHiddenGems}
          hiddenGemsCount={nearbyExperienceCount}
          openingVenueId={openingVenueId}
          savedOnly={savedOnly}
          onToggleSaved={() => setSavedOnly((v) => !v)}
          savedCount={favoriteCount}
        />
      </div>

      <VirtualizedVenueList
        ref={venueListRef}
        header={(
          <GuestMapListHeader
            loading={loading}
            featuredVenues={featuredVenues}
            userPosition={userPosition}
            onFeaturedVenueClick={scrollExpandedVenueIntoView}
            nearbyEvents={nearbyEvents}
            onEventVenueTap={onEventVenueTap}
            sortedVenuesCount={sortedVenues.length}
            locationStatus={locationStatus}
            onRequestLocation={() => requestLocation()}
          />
        )}
        venues={loading ? [] : sortedVenues}
        expandedVenueId={expandedVenueId}
        renderItem={renderVenueItem}
      />

      <MapFixedSection
        keyboardOpen={keyboardOpen}
        totalSectionHeight={totalSectionHeight}
        isDragging={isDragging}
        isExpanded={isExpanded}
        mapCanvasRef={mapCanvasRef}
        mapTheme={mapTheme}
        effectiveNavOffset={effectiveNavOffset}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        openMapFully={openMapFully}
        minimizeMap={minimizeMap}
        centerOnMe={centerOnMe}
        selectedVenueTags={selectedVenueTags}
        selectedExperienceTags={selectedExperienceTags}
        setSelectedVenueTags={setSelectedVenueTags}
        setSelectedExperienceTags={setSelectedExperienceTags}
        allVenues={allVenues}
        experiences={experiences}
        selectedMapVenueId={selectedMapVenueId}
        selectedExperienceId={selectedExperienceId}
        userPosition={userPosition}
        defaultCenter={defaultCenter}
        eventsByVenueId={eventsByVenueId}
        openVenueChat={openVenueChat}
        hasActivePass={hasActivePass}
        setSelectedMapVenueId={setSelectedMapVenueId}
        setSelectedExperienceId={setSelectedExperienceId}
        setPinnedVenueId={setPinnedVenueId}
        setExpandedVenueId={setExpandedVenueId}
        setExperiences={setExperiences}
        venueListRef={venueListRef}
      />

      <div style={{ display: keyboardOpen ? 'none' : 'contents' }}>
        <GuestBottomNav />
      </div>

      <DirectionsIntentModal />
      <GuestMapStylesheet isDarkMode={isDarkMode} />
    </div>
  );
}
